import {
	Action,
	Dispatch,
} from 'redux';
// Import services
import {
	StringService,
} from '@app/services';
// Import action type enumeration.
import ActionType from '@app/redux/actions/types';
import {
	NotificationType,
} from '@app/enumerations';
// Import api models.
import {
	AddressLocationAsMailingAddress,
	PerilDeductible,
} from '@app/models/api';
// Import action methods.
import {
	addNotification,
	endProcessing,
	startProcessing,
} from '@app/redux/actions/methods';
// import action parameters.
import {
	OverlayTargetParameters,
} from './ActionParametersBarrel';


// Clear New Business Application Preliminary Action.

type ClearNewBusinessApplicationPreliminaryAction = Action;

const clearNewBusinessApplicationPreliminaryAction: () =>
	ClearNewBusinessApplicationPreliminaryAction = () => ({
		type: ActionType.CLEAR_NEW_BUSINESS_APPLICATION_PRELIMINARY,
	});

const clearNewBusinessApplicationPreliminary = () =>
	async (dispatch: Dispatch): Promise<void> => {
		dispatch<ClearNewBusinessApplicationPreliminaryAction>(clearNewBusinessApplicationPreliminaryAction());
	};


// Save New Business Application Preliminary Perils Action.

type SaveNewBusinessApplicationPreliminaryPerilsAction = SaveNewBusinessApplicationPreliminaryPerilsParameters & Action;

interface SaveNewBusinessApplicationPreliminaryPerilsParameters extends OverlayTargetParameters {
	perilsDeductibles: PerilDeductible[];
}

const saveNewBusinessApplicationPreliminaryPerilsAction: (params: SaveNewBusinessApplicationPreliminaryPerilsParameters) =>
	SaveNewBusinessApplicationPreliminaryPerilsAction = (params: SaveNewBusinessApplicationPreliminaryPerilsParameters) => ({
		...params,
		type: ActionType.SAVE_NEW_BUSINESS_APPLICATION_PRELIMINARY_PERILS
	});

const saveNewBusinessApplicationPreliminaryPerils = (params: SaveNewBusinessApplicationPreliminaryPerilsParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-saveNewBusinessApplicationPreliminaryPerils`;

		try {
			// Start processing.
			await startProcessing({
				message: 'Please wait...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			// Save perils to the new business application preliminary state.
			dispatch<SaveNewBusinessApplicationPreliminaryPerilsAction>(saveNewBusinessApplicationPreliminaryPerilsAction(params));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: `Unable to process perils.`,
				secondaryMessage: error,
				notificationType: NotificationType.ERROR,
			})(dispatch);

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error.
			throw error;
		}
	};


// Save New Business Application Preliminary Address Action.

type SaveNewBusinessApplicationPreliminaryPropertyAddressAction = SaveNewBusinessApplicationPreliminaryPropertyAddressParameters & Action;

interface SaveNewBusinessApplicationPreliminaryPropertyAddressParameters extends OverlayTargetParameters {
	propertyAddress: AddressLocationAsMailingAddress;
}

const saveNewBusinessApplicationPreliminaryPropertyAddressAction: (params: SaveNewBusinessApplicationPreliminaryPropertyAddressParameters) =>
	SaveNewBusinessApplicationPreliminaryPropertyAddressAction = (params: SaveNewBusinessApplicationPreliminaryPropertyAddressParameters) => ({
		...params,
		type: ActionType.SAVE_NEW_BUSINESS_APPLICATION_PRELIMINARY_PROPERTY_ADDRESS
	});

const saveNewBusinessApplicationPreliminaryPropertyAddress = (params: SaveNewBusinessApplicationPreliminaryPropertyAddressParameters) =>
	async (dispatch: Dispatch): Promise<void> => {
		// Generate message id.
		const messageId: string = `${StringService.getInstance().hashObject(params)}-saveNewBusinessApplicationPreliminaryPropertyAddress`;
		try {
			// Start processing.
			await startProcessing({
				message: 'Please wait...',
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);

			// Save property address to the new business application preliminary state.
			dispatch<SaveNewBusinessApplicationPreliminaryPropertyAddressAction>(saveNewBusinessApplicationPreliminaryPropertyAddressAction(params));

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
		}
		catch (error: any) {
			// Add error notification.
			await addNotification({
				title: 'Error',
				primaryMessage: `Unable to process property address.`,
				secondaryMessage: error,
				notificationType: NotificationType.ERROR,
			})(dispatch);

			// End processing.
			await endProcessing({
				messageId: messageId,
				overlayTarget: params.overlayTarget,
			})(dispatch);
			// Throw error.
			throw error;
		}
	};


// Export actions.
export {
	ClearNewBusinessApplicationPreliminaryAction,
	SaveNewBusinessApplicationPreliminaryPerilsAction,
	SaveNewBusinessApplicationPreliminaryPropertyAddressAction,
};

// Export action methods.
export {
	clearNewBusinessApplicationPreliminary,
	saveNewBusinessApplicationPreliminaryPerils,
	saveNewBusinessApplicationPreliminaryPropertyAddress,
};

// Export action parameters.
export {
	SaveNewBusinessApplicationPreliminaryPerilsParameters,
	SaveNewBusinessApplicationPreliminaryPropertyAddressParameters,
};

// Export action factories.
export {

};